import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import type { ApiClient } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { HonestyBadge } from '../../ui/HonestyBadge';

// Small pieces every Atlas variant uses. The design draws its controls as
// <a href="#"> with inline styles (and a style-hover); these keep that markup and its
// exact style strings, so the page can be compared with the design pixel for pixel.

/** Load one route with the session's client. */
export function useApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true, pollMs?: number): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled, pollMs });
}

/** A design style string as a React style, with extra CSS appended. */
export const st = (css: string): CSSProperties => sx(css);

/**
 * The design's link-button: an anchor with its inline style, and the hover style when it
 * has one. `write` marks an action that changes something: it turns itself off, with the
 * reason on hover, for a read-only role or while viewing as another role.
 */
/** `border: 1px solid #ccc` as borderWidth, borderStyle and borderColor; anything else as it was. */
export function longhandBorder(st: CSSProperties): CSSProperties {
  const b = st.border;
  if (typeof b !== 'string') return st;
  const m = /^\s*(\S+)\s+(solid|dashed|dotted|double|none)\s+(.+?)\s*$/.exec(b);
  if (!m) return st;
  const { border: _border, ...rest } = st;
  void _border;
  return { ...rest, borderWidth: m[1], borderStyle: m[2] as CSSProperties['borderStyle'], borderColor: m[3] };
}

export function A({
  s,
  hover,
  onClick,
  children,
  write,
  disabledReason,
  title,
  label,
  role,
  selected,
  href,
}: {
  s: string;
  hover?: string;
  onClick?: () => void;
  children?: ReactNode;
  write?: boolean;
  disabledReason?: string | null;
  title?: string;
  label?: string;
  role?: string;
  selected?: boolean;
  href?: string;
}) {
  const [over, setOver] = useState(false);
  const roReason = useReadOnlyReason();
  const reason = (write ? roReason : null) ?? disabledReason ?? null;
  // With a hover, the base border is written out in longhands, so adding and removing the
  // hover's border-color never mixes it with the shorthand (React warns, and the border can
  // be left half-styled).
  const base = hover ? longhandBorder(sx(s)) : sx(s);
  const style = over && hover && !reason ? { ...base, ...sx(hover) } : base;
  const click = (e: MouseEvent) => {
    if (href) return;
    e.preventDefault();
    if (reason) return;
    onClick?.();
  };
  return (
    <a
      href={href ?? '#'}
      role={href ? undefined : (role ?? 'button')}
      aria-selected={selected}
      aria-disabled={reason ? true : undefined}
      aria-label={label}
      title={reason ?? title}
      style={style}
      onClick={click}
      onMouseEnter={hover ? () => setOver(true) : undefined}
      onMouseLeave={hover ? () => setOver(false) : undefined}
    >
      {children}
      {reason && <span className="sr-only"> ({reason})</span>}
    </a>
  );
}

/**
 * The design runs Operator, Facility Admin and Care Staff Atlas flush to the edges
 * (contentPad 0), banners included. The shell pads its content area, so while this page
 * is up the padding is lifted, and put back when it goes.
 */
export function useFlushContent(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const el = document.getElementById('console-content');
    if (!el) return;
    const prev = el.style.padding;
    el.style.padding = '0px';
    return () => {
      el.style.padding = prev;
    };
  }, [active]);
}

/**
 * The height of #console-content itself, as the design's `height:100%` on a page root
 * resolves it: the whole content pane, not what is left under a banner above the page
 * (the emergency line). With a banner up the page overflows and the pane scrolls, as the
 * design does. Null until measured.
 */
export function useContentHeight(): number | null {
  const [h, setH] = useState<number | null>(null);
  useEffect(() => {
    const el = document.getElementById('console-content');
    if (!el) return;
    const read = () => setH(el.clientHeight);
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return h;
}

// ── Time, as the design writes it ─────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Mar 2026" */
export function monthYear(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '–' : `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "November": the month Atlas started with them. */
export function monthName(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : FULL_MONTHS[d.getMonth()];
}

/** Whole minutes since an instant. */
export function minutesSince(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.max(0, Math.round((now - t) / 60000));
}

/** "4h", "2d": the design's short age. */
export function shortAge(iso: string | null | undefined, now = Date.now()): string {
  const m = minutesSince(iso, now);
  if (m === null) return '–';
  if (m < 60) return `${m}m`;
  if (m < 60 * 24) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / (60 * 24))}d`;
}

/** "Tuesday" within the last week (a week ago included), "12 Aug" before that, "today" for today. */
export function dayWord(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  const n = new Date(now);
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(n) - startOf(d)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days <= 7) return WEEKDAYS[d.getDay()];
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "9:10 am" (the design's at-home clock), "yesterday" before today. */
export function clockOrDay(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  const w = dayWord(iso, now);
  if (w !== 'today') return w;
  let h = d.getHours();
  const ap = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ap}`;
}

/** "19:04" */
export function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Months between an instant and now, whole. */
export function monthsSince(iso: string, now = Date.now()): number {
  const d = new Date(iso);
  const n = new Date(now);
  return Math.max(0, (n.getFullYear() - d.getFullYear()) * 12 + (n.getMonth() - d.getMonth()) - (n.getDate() < d.getDate() ? 1 : 0));
}

export function firstName(n: string | null | undefined): string {
  return (n ?? '').trim().split(/\s+/)[0] ?? '';
}

/**
 * SAMPLE, only when the route says its answer is not real. The design prints no badge on
 * real figures, so MEASURED and the rest stay hidden here.
 */
export function Sample({ real, figure }: { real: boolean | undefined; figure: string }) {
  if (real === undefined) return null;
  return <HonestyBadge flags={[{ real }]} figure={figure} hide={['MEASURED', 'MODELED', 'ESTIMATED']} />;
}
